import type { NotionTrashItem } from "@seagames/contracts";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PageIcon } from "../../components/icons/page-icon";
import { Icon } from "../../components/shell/icons";
import { Button } from "../../components/ui/button";
import { ConfirmDialog } from "../../components/ui/confirm-dialog";
import { EmptyState } from "../../components/ui/empty-state";
import { Spinner } from "../../components/ui/spinner";
import { toastApiError, toastSuccess } from "../../components/ui/toast";
import { formatDateTime, formatShort } from "../../lib/i18n/format";
import { deletePageForGood, restorePage } from "../../lib/notion/mutations";
import { trashQuery } from "../../lib/notion/queries";

/**
 * The trash: pages moved there, newest first (keyset pages). Each comes back
 * with its sub-pages, or is deleted for good — with its sub-pages, boards,
 * files and history.
 */
export const Route = createFileRoute("/_app/trash")({
	component: Trash,
});

function Trash() {
	const { t } = useTranslation("pages");
	const q = useInfiniteQuery(trashQuery());
	const items = q.data?.pages.flatMap((p) => p.items) ?? [];

	return (
		<div className="mx-auto w-full max-w-[52rem] px-[1.5rem] pt-[3rem] pb-[4rem] sm:px-[3rem]">
			<h1 className="flex items-center gap-[0.625rem] font-black text-[2rem] text-ink">
				<Icon name="trash" className="size-[1.75rem] text-muted" />
				{t("trash.title")}
			</h1>
			<p className="mt-[0.25rem] mb-[1.5rem] text-muted">{t("trash.subtitle")}</p>
			{q.isPending ? (
				<Spinner className="size-[1.25rem] text-muted" />
			) : q.isError ? (
				<button type="button" onClick={() => void q.refetch()} className="cursor-pointer text-conflict underline">
					{t("loadError")}
				</button>
			) : items.length === 0 ? (
				<EmptyState title={t("trash.emptyTitle")} body={t("trash.emptyBody")} />
			) : (
				<ul className="flex flex-col divide-y divide-rule rounded-card border border-rule">
					{items.map((item) => (
						<TrashRow key={item.id} item={item} />
					))}
				</ul>
			)}
			{q.hasNextPage && (
				<Button variant="outline" className="mt-[1rem]" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
					{t("trash.more")}
				</Button>
			)}
		</div>
	);
}

function TrashRow({ item }: { item: NotionTrashItem }) {
	const { t } = useTranslation("pages");
	const tn = useTranslation("nav").t;
	const [busy, setBusy] = useState<"restore" | "delete" | null>(null);
	const [confirm, setConfirm] = useState(false);
	const title = item.title.trim() || tn("untitled");

	const restore = async () => {
		setBusy("restore");
		try {
			await restorePage(item.id);
			toastSuccess(t("trash.restoredNamed", { title }));
		} catch (e) {
			toastApiError(e);
		} finally {
			setBusy(null);
		}
	};

	return (
		<li className="flex flex-wrap items-center gap-[0.75rem] px-[1rem] py-[0.75rem]">
			<PageIcon icon={item.icon} kind={item.kind} size={1.5} />
			<span className="flex min-w-0 flex-1 flex-col">
				<Link to="/p/$pageId" params={{ pageId: item.id }} className="truncate font-semibold text-ink hover:underline">
					{title}
				</Link>
				<span className="text-[0.8125rem] text-muted" title={formatDateTime(item.archived_at)}>
					{t("trash.by", { name: item.archived_by.name, time: formatShort(item.archived_at) })}
					{item.descendant_count > 0 && ` · ${t("trash.withSub", { count: item.descendant_count })}`}
				</span>
			</span>
			<Button size="sm" variant="outline" loading={busy === "restore"} disabled={busy !== null} onClick={() => void restore()}>
				<Icon name="restore" className="size-[0.875rem]" />
				{t("trash.restore")}
			</Button>
			<Button size="sm" variant="quiet" className="text-conflict" disabled={busy !== null} onClick={() => setConfirm(true)}>
				{t("trash.delete")}
			</Button>
			<ConfirmDialog
				open={confirm}
				title={t("trash.deleteTitle")}
				body={t("trash.deleteBodyNamed", { title, count: item.descendant_count })}
				confirmLabel={t("trash.delete")}
				tone="danger"
				pending={busy === "delete"}
				onCancel={() => setConfirm(false)}
				onConfirm={async () => {
					setBusy("delete");
					try {
						await deletePageForGood(item.id);
						setConfirm(false);
					} catch (e) {
						toastApiError(e);
					} finally {
						setBusy(null);
					}
				}}
			/>
		</li>
	);
}
